/* eslint-disable no-nested-ternary */
import { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Card, Typography, Box, Button, Stack, CircularProgress } from '@mui/material';
// components
import Iconify from 'src/components/Iconify';
// redux
import { dispatch } from 'src/redux/store';
import { updateError } from 'src/redux/slices/user';
import { setCurrentTreatmentDataGlobal } from 'src/redux/slices/multiTreatment';
// hooks / api
import { parseApiDate } from 'src/utils/smartReminderRules';
import useTreatmentPaymentStatus from 'src/hooks/useTreatmentPaymentStatus';
import { resendPaymentLink, describeChannelState } from 'src/sections/@dashboard/payment/paymentLinkApi';
import CreateSendPaymentLinkDialog from 'src/sections/@dashboard/payment/CreateSendPaymentLinkDialog';
import {
    CHANNEL_LABEL,
    DeliveryChip,
    newestDeliveriesByChannel,
    newestDeliveryOverall,
} from 'src/sections/@dashboard/payment/deliveryChips';
// styles
import {
    leadAppointmentCardStyle,
    lockIconMainBoxStyle,
    lockIconInnerBoxStyle,
    priceValueTextStyle,
    takeDepositButton,
} from '../../../styles/OverView';
import { typoSpanStyle } from '../../../styles/LeadDetailView';
import { common } from '../../../theme/palette';

// Card visuals per state — same card shell as the original green "Appointment
// booked" card (leadAppointmentCardStyle / lockIconMainBoxStyle), re-tinted
// per state. Hex values mirror the theme's warning/error palette entries
// (src/theme/palette.js) rather than introducing new ones.
const STATE_VISUALS = {
    none: { label: '#00A76F', icon: '#00A76F', iconBg: 'rgba(0, 167, 111, 0.12)', bg: 'rgba(0, 167, 111, 0.06)', border: 'rgba(0, 167, 111, 0.16)' },
    paid: { label: '#00A76F', icon: '#00A76F', iconBg: 'rgba(0, 167, 111, 0.12)', bg: 'rgba(0, 167, 111, 0.06)', border: 'rgba(0, 167, 111, 0.16)' },
    awaiting: { label: '#B76E00', icon: '#B76E00', iconBg: 'rgba(255, 171, 0, 0.12)', bg: 'rgba(255, 171, 0, 0.06)', border: 'rgba(255, 171, 0, 0.16)' },
    expired: { label: '#B71D18', icon: '#B71D18', iconBg: 'rgba(255, 86, 48, 0.12)', bg: 'rgba(255, 86, 48, 0.06)', border: 'rgba(255, 86, 48, 0.16)' },
    noLink: { label: '#637381', icon: '#637381', iconBg: 'rgba(145, 158, 171, 0.12)', bg: 'rgba(145, 158, 171, 0.06)', border: 'rgba(145, 158, 171, 0.16)' },
};

const formatAmount = (n) => {
    const num = Number(n) || 0;
    return Number.isInteger(num) ? String(num) : num.toFixed(2);
};

const lowerCaseType = (t) => (typeof t === 'string' ? t.toLowerCase() : '');

// API datetimes come back as DRF's UK format (e.g. "24-09-2026
// 11:43:49.914866Z" — a literal trailing Z, not UTC) which bare
// moment()/new Date() can't parse — use the shared parseApiDate. Falls back
// to empty string rather than "Invalid date" if parsing ever fails.
const formatRelative = (value) => {
    const parsed = parseApiDate(value);
    return parsed ? parsed.fromNow() : '';
};
const formatDate = (value, fmt) => {
    const parsed = parseApiDate(value);
    return parsed ? parsed.format(fmt) : '';
};

// newestDeliveriesByChannel / newestDeliveryOverall now live in the shared
// src/sections/@dashboard/payment/deliveryChips module (also used by the
// Payments-tab PaymentCard) — see that module's docstring for rationale.

export default function BookedPaymentStatusCard({
    leadDetail,
    bookedLeadAppointment,
    bookedTreatment,
    practiceId,
    handleMenuItems,
    onChangeTab,
}) {
    const leadId = leadDetail?.id;
    const treatmentId = bookedTreatment?.id;
    // The specific appointment this card displays — a treatment can have
    // more than one appointment (e.g. a rebooked consultation), each with
    // its own deposit, so the payment status below is scoped to THIS one
    // (contract: Payment gains `appointment`) rather than to every payment
    // across the whole treatment.
    const appointment = bookedLeadAppointment?.[0];

    const {
        loading, state, primary, session, paidTotal, awaiting, refresh,
    } = useTreatmentPaymentStatus(practiceId, leadId, treatmentId, appointment?.id);

    const [resending, setResending] = useState(false);
    const [sendDialogOpen, setSendDialogOpen] = useState(false);

    // Derived from ALL deliveries on the session — not just deliveries[0] —
    // so a payment sent via both Email and WhatsApp shows both, and a
    // channel's old failure doesn't hide its later success (or vice versa).
    const sessionDeliveryByChannel = newestDeliveriesByChannel(session?.deliveries);
    const sessionChannelsOrdered = ['email', 'whatsapp'].filter((c) => sessionDeliveryByChannel[c]);
    const sessionNewestDelivery = newestDeliveryOverall(session?.deliveries);

    const potential = Number(bookedTreatment?.custom_price || bookedTreatment?.price || 0);
    const bookedLine = (
        <Typography sx={{ fontSize: '13px', color: common.darkGreenContrast, fontWeight: 500, lineHeight: '18px' }}>
            <span style={typoSpanStyle}>{bookedTreatment?.treatment || ''}</span> booked for{' '}
            <span style={typoSpanStyle}>{moment(appointment?.datetime, 'DD/MM/YYYY').format('DD MMM YYYY')}</span> at{' '}
            <span style={typoSpanStyle}>{moment(appointment?.datetime, 'DD/MM/YYYY hh:mm:ss').format('hh:mm a')}</span>
        </Typography>
    );

    const handleTakeDeposit = useCallback(() => {
        dispatch(setCurrentTreatmentDataGlobal({
            lead_id: leadDetail?.id || '',
            first_name: leadDetail?.first_name || '',
            last_name: leadDetail?.last_name || '',
            treatmentData: bookedTreatment,
            // Contract: Payment create accepts an optional `appointment` id —
            // this is the exact appointment this card displays (bookedLeadAppointment[0]),
            // so the payment PaymentOption.jsx creates should be linked to it.
            // PaymentOption prefers this over its own nearest-upcoming-BOOKED default.
            appointmentId: appointment?.id ?? null,
        }));
        handleMenuItems('Take a Payment');
    }, [leadDetail, bookedTreatment, handleMenuItems, appointment]);

    const handleCopyLink = useCallback(async () => {
        if (!session?.url) return;
        try {
            if (typeof navigator !== 'undefined' && navigator.clipboard) {
                await navigator.clipboard.writeText(session.url);
            }
            dispatch(updateError({ success: true, message: 'Payment link copied' }));
        } catch {
            dispatch(updateError({ success: false, message: 'Could not copy link' }));
        }
    }, [session]);

    const handleResend = useCallback(async () => {
        if (!primary?.id) return;
        const finalChannels = sessionChannelsOrdered.length > 0 ? sessionChannelsOrdered : ['email'];
        setResending(true);
        try {
            const payload = { channels: finalChannels };
            const defaultEmail = leadDetail?.lead_contact?.email || leadDetail?.email || '';
            const defaultPhone = leadDetail?.lead_contact?.phone || leadDetail?.phone || leadDetail?.phone_number || '';
            if (finalChannels.includes('email') && defaultEmail) payload.email = defaultEmail;
            if (finalChannels.includes('whatsapp') && defaultPhone) payload.phone = defaultPhone;

            const res = await resendPaymentLink(practiceId, leadId, treatmentId, primary.id, payload);
            if (res?.ok && res.status === 201 && res.data) {
                const outcomes = finalChannels
                    .map((c) => `${CHANNEL_LABEL[c] || c}: ${describeChannelState(res.data.channels?.[c]).label}`)
                    .join(', ');
                dispatch(updateError({ success: true, message: `Resent — ${outcomes}` }));
                refresh();
            } else if (res?.status === 409) {
                // link_expired — the payment moved from awaiting to expired server-side.
                dispatch(updateError({ success: false, message: res?.error || 'This payment link has expired.' }));
                refresh();
            } else {
                dispatch(updateError({ success: false, message: res?.error || 'Could not resend payment link.' }));
            }
        } catch {
            dispatch(updateError({ success: false, message: 'Could not resend payment link.' }));
        } finally {
            setResending(false);
        }
    }, [practiceId, leadId, treatmentId, primary, sessionChannelsOrdered, leadDetail, refresh]);

    const extraAwaitingCount = state === 'awaiting' ? Math.max(0, (awaiting?.length || 0) - 1) : 0;

    const visuals = STATE_VISUALS[loading ? 'none' : state] || STATE_VISUALS.none;

    if (!bookedLeadAppointment?.length) return null;

    return (
        <Card sx={{ ...leadAppointmentCardStyle, bgcolor: visuals.bg, border: '1px solid', borderColor: visuals.border }}>
            <Box sx={lockIconMainBoxStyle}>
                <Box sx={{ ...lockIconInnerBoxStyle, bgcolor: visuals.iconBg }}>
                    <Iconify icon={'ic:baseline-lock'} sx={{ color: visuals.icon }} width={20} height={20} />
                </Box>
            </Box>

            {/* Loading (or fetch-error fallback): render the static "none" card so
                there's no flicker/blank while the payment status loads. */}
            {(loading || state === 'none') && (
                <>
                    <Typography sx={{ fontSize: '13px', color: '#00A76F', fontWeight: 600 }}>Appointment booked</Typography>
                    <Typography sx={priceValueTextStyle}>£{formatAmount(potential) || '0'} Potential value.</Typography>
                    {bookedLine}
                    <Button variant='contained' sx={takeDepositButton} onClick={handleTakeDeposit} data-testid="take-deposit-btn">
                        <Iconify icon={'codicon:arrow-down'} sx={{ mr: 0.5 }} width={16} height={16} /> Take deposit
                    </Button>
                </>
            )}

            {!loading && state === 'awaiting' && (
                <>
                    <Typography sx={{ fontSize: '13px', color: visuals.label, fontWeight: 600 }} data-testid="payment-card-label">
                        Waiting for payment
                    </Typography>
                    <Typography sx={priceValueTextStyle}>
                        £{formatAmount(primary?.amount)} {lowerCaseType(primary?.payment_type)} requested
                    </Typography>
                    {bookedLine}
                    {session && (
                        <Stack spacing={0.75} sx={{ mt: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                                Sent via {sessionChannelsOrdered.map((c) => CHANNEL_LABEL[c] || c).join(' + ') || 'Email'}
                                {sessionNewestDelivery?.created_at ? ` · ${formatRelative(sessionNewestDelivery.created_at)}` : ''}
                            </Typography>
                            <Stack direction="row" spacing={0.75} flexWrap="wrap">
                                {sessionChannelsOrdered.map((channel) => (
                                    <DeliveryChip key={channel} channel={channel} delivery={sessionDeliveryByChannel[channel]} />
                                ))}
                            </Stack>
                            {sessionChannelsOrdered.some((c) => ['failed', 'blocked'].includes(sessionDeliveryByChannel[c]?.state)) && (
                                <Typography variant="caption" color="error">
                                    {sessionChannelsOrdered.map((c) => {
                                        const d = sessionDeliveryByChannel[c];
                                        return d && ['failed', 'blocked'].includes(d.state) ? `${CHANNEL_LABEL[c]} failed: ${d.error || 'delivery blocked'}` : null;
                                    }).filter(Boolean).join('; ')}
                                </Typography>
                            )}
                        </Stack>
                    )}
                    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                        <Button
                            variant='contained'
                            sx={{ ...takeDepositButton, mt: 0, bgcolor: visuals.label, '&:hover': { bgcolor: visuals.label } }}
                            onClick={handleResend}
                            disabled={resending}
                            data-testid="resend-payment-link-btn"
                        >
                            {resending ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Resend'}
                        </Button>
                        {session?.is_open && session?.url && (
                            <Button
                                variant='outlined'
                                sx={{ mt: 0, whiteSpace: 'nowrap', flexShrink: 0 }}
                                onClick={handleCopyLink}
                                data-testid="copy-payment-link-btn"
                            >
                                Copy link
                            </Button>
                        )}
                    </Stack>
                    {extraAwaitingCount > 0 && (
                        <Typography
                            variant="caption"
                            sx={{ display: 'block', mt: 1, cursor: 'pointer', textDecoration: 'underline' }}
                            onClick={() => onChangeTab && onChangeTab('', 'Payment and refunds')}
                            data-testid="more-awaiting-link"
                        >
                            +{extraAwaitingCount} more awaiting
                        </Typography>
                    )}
                </>
            )}

            {!loading && state === 'expired' && (
                <>
                    <Typography sx={{ fontSize: '13px', color: visuals.label, fontWeight: 600 }} data-testid="payment-card-label">
                        Payment link expired
                    </Typography>
                    <Typography sx={priceValueTextStyle}>
                        £{formatAmount(primary?.amount)} {lowerCaseType(primary?.payment_type)} not paid
                    </Typography>
                    {bookedLine}
                    <Button
                        variant='contained'
                        sx={{ ...takeDepositButton, bgcolor: visuals.label, '&:hover': { bgcolor: visuals.label } }}
                        onClick={() => setSendDialogOpen(true)}
                        data-testid="send-new-link-btn"
                    >
                        Send new link
                    </Button>
                </>
            )}

            {!loading && state === 'paid' && (
                <>
                    <Typography sx={{ fontSize: '13px', color: visuals.label, fontWeight: 600 }} data-testid="payment-card-label">
                        {lowerCaseType(primary?.payment_type) === 'deposit' ? 'Deposit paid ✓' : `${primary?.payment_type || 'Payment'} paid ✓`}
                    </Typography>
                    <Typography sx={priceValueTextStyle}>£{formatAmount(paidTotal)} paid</Typography>
                    <Typography sx={{ fontSize: '13px', color: common.darkGreenContrast, fontWeight: 500 }}>
                        on {formatDate(primary?.paid_at, 'DD MMM YYYY')}
                    </Typography>
                    {potential > paidTotal && (
                        <Typography sx={{ fontSize: '12px', color: 'text.secondary', mb: 0.5 }}>
                            £{formatAmount(potential - paidTotal)} of £{formatAmount(potential)} remaining
                        </Typography>
                    )}
                    {bookedLine}
                    <Button variant='contained' sx={takeDepositButton} onClick={handleTakeDeposit} data-testid="take-another-payment-btn">
                        <Iconify icon={'codicon:arrow-down'} sx={{ mr: 0.5 }} width={16} height={16} /> Take another payment
                    </Button>
                </>
            )}

            {!loading && state === 'noLink' && (
                <>
                    <Typography sx={{ fontSize: '13px', color: visuals.label, fontWeight: 600 }} data-testid="payment-card-label">
                        Payment requested — no link sent
                    </Typography>
                    {bookedLine}
                    <Button
                        variant='contained'
                        sx={{ ...takeDepositButton, bgcolor: visuals.label, '&:hover': { bgcolor: visuals.label } }}
                        onClick={() => setSendDialogOpen(true)}
                        data-testid="send-link-btn"
                    >
                        Send link
                    </Button>
                </>
            )}

            {sendDialogOpen && (
                <CreateSendPaymentLinkDialog
                    open={sendDialogOpen}
                    onClose={() => setSendDialogOpen(false)}
                    practiceId={practiceId}
                    leadId={leadId}
                    treatmentId={treatmentId}
                    payment={primary}
                    leadDetail={leadDetail}
                    replaceExpired={state === 'expired'}
                    onSuccess={refresh}
                    appointmentId={appointment?.id ?? null}
                />
            )}
        </Card>
    );
}

BookedPaymentStatusCard.propTypes = {
    leadDetail: PropTypes.object,
    bookedLeadAppointment: PropTypes.array,
    bookedTreatment: PropTypes.object,
    practiceId: PropTypes.any,
    handleMenuItems: PropTypes.func,
    onChangeTab: PropTypes.func,
};

BookedPaymentStatusCard.defaultProps = {
    leadDetail: null,
    bookedLeadAppointment: [],
    bookedTreatment: null,
    practiceId: null,
    handleMenuItems: () => {},
    onChangeTab: null,
};
